import { useEffect, useState } from "react";
import axios from "../.../../../api/apiClient";
import { Select } from "../Select";
import { domain, token } from "../../components/Security";

export function ClassificationSelect({
  // Select component props
  label,
  isRequired = false,
  isDisabled = false,
  isEditMode = false,
  value,
  onChange,
  setValue,
  placeholder = "Choose classification",
  className = "",
  parentClassName = "",
  error = false,
  errorMessage = "",
  isLoading = false,
  allowSelectAll = false,

  // Legacy props for backward compatibility
  classificationId,        // legacy selected value
  setClassificationId,    // legacy setter
  isEditable,             // legacy editability

  // Filter props
  payTypeId,              // filter by pay type
  user,                   // user for filtering

  // Additional props
  includeInactive = false,

  ...props
}) {
  const [classifications, setClassifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [options, setOptions] = useState([]);

  // Legacy mode detection
  const isLegacyMode = classificationId !== undefined || setClassificationId !== undefined;

  // Determine value for Select component
  const selectValue = isLegacyMode ? classificationId?.toString() || "" : value || "";

  // Determine disabled state
  const disabled = isEditMode || isDisabled || loading || isLoading ||
    (isLegacyMode && isEditable === false) || !payTypeId;

  useEffect(() => {
    const fetchData = async () => {
      // Don't fetch if no payTypeId (maintains original behavior)
      if (!payTypeId || !user?.username) {
        setClassifications([]);
        setOptions([]);
        return;
      }

      try {
        setLoading(true);
        const endpoint = includeInactive
          ? `/api/Classification/username/${user.username}`
          : `/api/Classification/active/username/${user.username}`;

        const response = await axios.get(domain + endpoint, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        // Apply payType filtering (maintains original filtering logic)
        let filteredData = response.data;

        setClassifications(filteredData);

        // Transform to options format for Select component
        const transformedOptions = filteredData.map(classification => {
          let label = classification.description || "";

          // Add inactive indicator
          if (includeInactive && !classification.isActive) {
            label += " [Inactive]";
          }

          return {
            value: classification.id.toString(),
            label: label,
            rawData: classification
          };
        });

        setOptions(transformedOptions);
      } catch (err) {
        console.error('Error fetching classifications:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [payTypeId, user?.username, includeInactive]);

  const handleChange = (e) => {
    const selectedValue = e.target.value;

    if (isLegacyMode) {
      // Legacy mode: maintain existing behavior
      if (setClassificationId) {
        // Check for both empty string and "0" (if allowSelectAll is used)
        const isAllOption = allowSelectAll && (selectedValue === "" || selectedValue === "0");
        setClassificationId(isAllOption ? null : selectedValue);
      }
    } else {
      // New mode: handle both onChange and setValue patterns
      if (onChange) {
        onChange(e);
      } else if (setValue) {
        setValue(selectedValue);
      }
    }
  };

  // Show placeholder when payTypeId is not selected
  if (!payTypeId) {
    return (
      <Select
        label={label}
        isRequired={isRequired}
        isDisabled={true}
        isEditMode={isEditMode}
        value=""
        onChange={handleChange}
        options={[]}
        placeholder="Select pay type first"
        className={className}
        parentClassName={parentClassName}
        error={error}
        errorMessage={errorMessage}
        {...props}
      />
    );
  }

  // Handle loading state
  if (loading) {
    return (
      <Select
        label={label}
        isRequired={isRequired}
        isDisabled={true}
        isEditMode={isEditMode}
        value={selectValue}
        onChange={handleChange}
        options={[]}
        placeholder="Loading classifications..."
        className={className}
        parentClassName={parentClassName}
        error={error}
        errorMessage={errorMessage}
        allowSelectAll={allowSelectAll}
        {...props}
      />
    );
  }

  return (
    <Select
      label={label}
      isRequired={isRequired}
      isDisabled={disabled}
      isEditMode={isEditMode}
      value={selectValue}
      onChange={handleChange}
      options={options}
      placeholder={placeholder}
      className={className}
      parentClassName={parentClassName}
      error={error}
      errorMessage={errorMessage}
      allowSelectAll={allowSelectAll}
      {...props}
    />
  );
}